import React, { useContext, useEffect, useRef, useState } from "react";
import {
  notifyError,
  roundToThreeDecimal,
} from "../../../../../helpers/helpers";
import { useMediaQuery } from "react-responsive";
import { useDispatch, useSelector } from "react-redux";
import { postLiveRoomDataForProduct } from "../../../../../redux/action/liveRoom";
import ConnectionStatus from "./ConnectionStatus";
import { ClipLoader } from "react-spinners";
import { getSocket } from "../../../../../helpers/SingletonSocketService";
import { globalContext } from "../../../../../context/ContextProvider";
import { useParams } from "react-router-dom";
import ProductMinAndHighestPrice from "./ProductMinAndHighestPrice";

function ProductLiveBidingArea({
  liveOfferData,
  timeEnded,
  startTimer,
  setBidderDetails,
  index,
}) {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const { id } = useParams();
  const isDesktop = useMediaQuery({ query: "(min-width: 1600px)" });
  const isLaptop = useMediaQuery({ query: "(max-width: 1200px)" });
  const isSmallTablet = useMediaQuery({ query: "(max-width: 750px)" });
  const [bidAmount, setBidAmount] = useState("");
  const [bidPriceUpdateValue, setBidPriceUpdateValue] = useState({});
  const [myCurrentBid, setMyCurrentBid] = useState(0);
  const { profile } = useSelector((state) => state?.profileStore);
  const { loading, loadingId } = useSelector((state) => state.liveAuctionList);
  const { connectingWithLiveRoom } = useContext(globalContext);

  //socket connection for live room
  useEffect(() => {
    const socket = getSocket();
    socket?.on("newBid", (data) => {
      if (data?.offerId === liveOfferData?._id) {
        const { bidHistory, ...restData } = data;
        setBidPriceUpdateValue(restData);
        setBidderDetails([
          {
            myBidColor:
              data?.bidDetails?.organization === profile?.organization,
            bidHistory,
          },
        ]);
      }
    });

    document.body.style.overflow = "auto";
  }, [liveOfferData, profile, setBidderDetails]);


  //handle submit bid functionality for live room
  const handleBidUpdateValue = async (e) => {
    e.preventDefault();
    const FirstBidTime =
      bidPriceUpdateValue?.mimumestBid === 0
        ? liveOfferData?.startingPrice
        : bidPriceUpdateValue?.mimumestBid;
    const value = Number(bidAmount);
    const { minimumBidDecrease } = liveOfferData;
    const difference = +(value - FirstBidTime).toFixed(10);
    const multiplied = +(difference / minimumBidDecrease).toFixed(10);
    const nearestInteger = Math.round(multiplied);
    const remainder = Math.abs(multiplied - nearestInteger);
    const tolerance = 0.0001;

    if (timeEnded) {
      return notifyError("Time has ended");
    } else if (startTimer) {
      return notifyError("Auction for this lot has not started yet");
    } else if (
      bidPriceUpdateValue?.mimumestBid !== 0 &&
      value >
      +(bidPriceUpdateValue?.mimumestBid -
        (liveOfferData?.minimumBidDecrease || 0)

      ).toFixed(10)
    ) {
      return notifyError(
        `Bid amount should be less than highest bid minus minimum bid decrement. Maximum amount is ${+(
          (bidPriceUpdateValue?.mimumestBid || 0) - (liveOfferData?.minimumBidDecrease || 0)

        ).toFixed(10)}`
      );
    } else if (
      value >
      +(
        liveOfferData?.startingPrice - (liveOfferData?.minimumBidDecrease || 0)
      ).toFixed(10)
    ) {
      return notifyError(
        `Bid amount should be less than Starting price minus minimum bid decrement. Maximum amount is ${+(
          liveOfferData?.startingPrice -
          (liveOfferData?.minimumBidDecrease || 0)
        ).toFixed(10)}`
      );
    } else if (Math.abs(remainder) > tolerance) {
      const { minimumBidDecrease } = liveOfferData;
      const nearestMultiple = Math.ceil(difference / minimumBidDecrease);
      const nearestValidBid =
        FirstBidTime + nearestMultiple * minimumBidDecrease;
      const previousValidBid = nearestValidBid - minimumBidDecrease;

      const isDecimal = !Number.isInteger(minimumBidDecrease);
      const format = (num) => (isDecimal ? num.toFixed(2) : num.toFixed(0));

      return notifyError(
        `Bid amount should be in multiples of ₹${format(
          minimumBidDecrease
        )}. ` +
        `Nearest valid bid is ₹${format(
          nearestValidBid
        )} or ₹${format(previousValidBid)}`
      );
    } else {
      dispatch(
        postLiveRoomDataForProduct(value, true, id, liveOfferData?._id)
      ).then(() => {
        inputRef.current?.blur();
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  //find the current bid of the user after refesh the page
  useEffect(() => {
    if (liveOfferData) {
      setBidPriceUpdateValue({
        mimumestBid: liveOfferData?.mimumestBid || 0,
        offerId: liveOfferData?._id,
        myLastBid: liveOfferData?.myCurrentBidAmount,
        bidDetails: {
          organization: profile?.organization,
          bidAmount: liveOfferData?.myCurrentBidAmount,
          autoBidAmount: null,
        },
      });
      setBidderDetails([
        { myBidColor: true, bidHistory: liveOfferData?.bidHistory },
      ]);
    }
  }, [liveOfferData, profile, setBidderDetails]);

  //find the current bid of the user
  useEffect(() => {
    if (
      profile?.organization === bidPriceUpdateValue?.bidDetails?.organization
    ) {
      setMyCurrentBid(bidPriceUpdateValue?.myLastBid);
    }
  }, [bidPriceUpdateValue, profile]);

  useEffect(() => {
    if (bidPriceUpdateValue?.mimumestBid === 0) {
      setBidAmount(liveOfferData?.startingPrice);
    } else {
      setBidAmount(bidPriceUpdateValue?.mimumestBid);
    }
  }, [bidPriceUpdateValue, liveOfferData, profile, myCurrentBid]);


  if (connectingWithLiveRoom) {
    return <ConnectionStatus />;
  }

  return (
    <div
      className={`${isSmallTablet
        ? "valign-wrapper gap-2 column justify-center"
        : isLaptop && "valign-wrapper justify-center flex-wrap gap-3"
        }  `}
      style={{
        display: !isLaptop && "grid",
        gridTemplateColumns: "400px 1fr 1fr",
        padding: "1rem",
      }}
    >
      <ProductMinAndHighestPrice
        liveOfferData={liveOfferData}
        bidPriceUpdateValue={bidPriceUpdateValue}
        timeEnded={timeEnded}
        startTimer={startTimer}
        index={index}
      />

      {/* {!liveOfferData?.isPaid ? (
        <div className="valign-wrapper justify-center"
          style={{
            opacity: timeEnded && 0.3,
            order: 3,
          }}
        >
          <button
            className={`pointergrey lighten-2 grey-text white-text cercle-purple font-18px`}
            style={{
              padding: "10px 20px",
              borderRadius: "8px",
              width: "200px",
            }}
            disabled={timeEnded}
            onClick={() => setIsModalOpen(true)}
          >
           Pay EMD Now
          </button>
        </div>
      ) : ( */}
      <div
        className="flex column align-center gap-10px"
        style={{
          opacity: timeEnded && 0.3,
          order: 3,
        }}
      >
        <h5 className={`margin-0px font-18px`}>Enter your bid</h5>
        <form
          onSubmit={(e) => handleBidUpdateValue(e)}
          className="full-width column flex align-center gap-1 justify-center"
        >
          <div className="flex gap-1">
            <button
              type="button"
              className="circle valign-wrapper justify-center pointer circle"
              style={{
                width: "45px",
                height: "45px",
                backgroundImage:
                  (bidPriceUpdateValue?.mimumestBid === bidAmount || liveOfferData?.startingPrice === bidAmount)
                    ? "linear-gradient(rgb(150, 150, 150),rgb(230, 225, 235))"
                    : "linear-gradient(#4C2171, #6F2DA8)",
              }}
              disabled={timeEnded}
              onKeyDown={handleKeyDown}
              onClick={() =>
                setBidAmount((prev) => {
                  const current = Number(prev) || 0;
                  const increment = liveOfferData?.minimumBidDecrease;
                  const maximumBidAllowed = bidPriceUpdateValue?.mimumestBid === 0
                    ? liveOfferData?.startingPrice
                    : bidPriceUpdateValue?.mimumestBid;;
                  const updated = +(current + increment).toFixed(10);
                  return Math.min(updated, maximumBidAllowed);
                })
              }
            >
              <span
                className="white-text"
                style={{ fontSize: "45px", fontWeight: 600 }}
              >
                +
              </span>
            </button>
            <span className="border-radius-12 flex column valign-wrapper justify-center">
              <input
                ref={inputRef}
                value={bidAmount}
                type="number"
                step="0.01"
                min="0"
                disabled={timeEnded}
                className="browser-default white font-25px"
                onChange={(e) => setBidAmount(e.target.value)}
                style={{
                  width: isDesktop ? "230px" : "200px",
                  boxShadow:
                    "-4px -5px 10px 0 white inset, 3px 3px 8px 0 rgba(0, 0, 0, 0.25) inset",
                  height: "50px",
                  fontWeight: "600",
                  borderRadius: 10,
                  padding: "12px 12px 12px 15px",
                  textAlign: "center",
                }}
              />

              <span className={`current-bid-amount`}>
                <p className={`small-text`}>
                  Your Bid Amount: {roundToThreeDecimal(myCurrentBid) || 0}
                </p>
              </span>
            </span>
            <button
              type="button"
              onKeyDown={handleKeyDown}
              className="circle valign-wrapper justify-center pointer"
              style={{
                width: "45px",
                height: "45px",
                backgroundImage: "linear-gradient(#4C2171, #6F2DA8)",
              }}
              disabled={timeEnded}
              onClick={() =>
                setBidAmount((prev) => {
                  const current = Number(prev) || 0;
                  const decrement = liveOfferData?.minimumBidDecrease || 0;
                  const highestBid = bidPriceUpdateValue?.mimumestBid === 0
                    ? liveOfferData?.startingPrice
                    : bidPriceUpdateValue?.mimumestBid;
                  const updated = +(current - decrement).toFixed(10);
                  return Math.min(highestBid, updated);
                })
              }
            >
              <span
                className="white-text"
                style={{ fontSize: "45px", fontWeight: 600 }}
              >
                -
              </span>
            </button>
          </div>
          <button
            className={`pointergrey lighten-2 grey-text white-text cercle-purple font-20px`}
            type="submit"
            style={{
              padding: "10px 40px",
              borderRadius: "8px",
              width: "150px",
              height: "45px",
            }}
            disabled={
              timeEnded || (loading && loadingId === liveOfferData?._id)
            }
          >
            {loading && loadingId === liveOfferData?._id ? (
              <ClipLoader color="#fff" size={20} />
            ) : (
              "Submit"
            )}
          </button>
        </form>
      </div>
      {/* )} */}
    </div>
  );
}

export default ProductLiveBidingArea;
